import { Checkbox, Menu, Tooltip } from "antd";
import { Button } from "antd";
import { Dropdown, Flex } from "antd";
import React, { FC, useEffect, useState } from "react";
import { ReactComponent as ColumnSelection } from "../../utils/svgIcons/fluent_column-edit-24-regular.svg";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker } from "antd";
import { IHospital } from "../../interfaces/IHospital";
import { Select } from "antd";
import "./FiltersManagementDashboard.css";

// no RangePicker or Typography used

type FiltersManagementDashboardProps = {
  selectedHospital: IHospital | null;
  handleHospitalChange: (hospitalId: number) => void;
  availableHospitals: Array<IHospital> | undefined;
  handleFiltersChange: any;
  filters: any;
  selectedColumnKeys: string[];
  setSelectedColumnKeys: CallableFunction;
  columnKeyTitles: any;
  setFilters: any;
};

const FiltersManagementDashboard: FC<FiltersManagementDashboardProps> = ({
  selectedColumnKeys,
  selectedHospital,
  setSelectedColumnKeys,
  handleFiltersChange,
  handleHospitalChange,
  availableHospitals,
  filters,
  columnKeyTitles,
  setFilters,
}) => {
  // no manual range; quarter derives from month selection

  const [monthYear, setMonthYear] = useState<Dayjs | null>(
    filters.monthYear ? dayjs(filters.monthYear, "MM-YYYY") : null
  );

  // quarter is auto-derived into filters (no manual picker)

  const [dropdownVisible, setDropdownVisible] = useState(false);

  const toggleSelectAll = (checked: boolean) => {
    setSelectedColumnKeys(
      checked ? columnKeyTitles?.map((col: any) => col?.key as string) : []
    );
  };

  const menu = (
    <Menu selectable={false}>
      <div
        onClick={() => {
          const isChecked =
            selectedColumnKeys.length !== columnKeyTitles.length;
          toggleSelectAll(isChecked);
          setDropdownVisible(true);
        }}
      >
        <Menu.Item
          key="select-all"
          onClick={(e) => e?.domEvent?.preventDefault()}
        >
          <Checkbox
            checked={selectedColumnKeys.length === columnKeyTitles.length}
            indeterminate={
              selectedColumnKeys.length > 0 &&
              selectedColumnKeys.length < columnKeyTitles.length
            }
          >
            Select All
          </Checkbox>
        </Menu.Item>
      </div>
      <Menu.Divider />
      <div style={{ maxHeight: "250px", overflowY: "auto" }}>
        {columnKeyTitles?.map((col: any) => (
          <div
            onClick={(e) => {
              e.preventDefault();
              // e.stopPropagation();
              setDropdownVisible(true);

              const isChecked = selectedColumnKeys?.includes(
                col?.key as string
              );
              const newSelectedKeys = isChecked
                ? selectedColumnKeys.filter((key) => key !== col?.key)
                : [...selectedColumnKeys, col?.key as string];

              setSelectedColumnKeys(newSelectedKeys);
            }}
          >
            <Menu.Item key={col?.key}>
              <Checkbox
                checked={selectedColumnKeys?.includes(col?.key as string)}
              >
                {col?.title}
              </Checkbox>
            </Menu.Item>
          </div>
        ))}
      </div>
    </Menu>
  );

  // no range handler used

  const handleMonthYearChange = (date: Dayjs | null) => {
    setMonthYear(date);
    const derivedQuarter = date
      ? `Q${Math.ceil(Number(date.format("M")) / 3)}-${date.format("YYYY")}`
      : null;
    setFilters({
      ...filters,
      monthYear: date ? date.format("MM-YYYY") : null,
      quarter: derivedQuarter,
    });
    handleFiltersChange({
      ...filters,
      monthYear: date ? date.format("MM-YYYY") : null,
      quarter: derivedQuarter,
    });
  };

  const getQuarterLabel = (q: number, year: number) => {
    const map: Record<number, string> = {
      1: "JFM",
      2: "AMJ",
      3: "JAS",
      4: "OND",
    };

    return `${map[q]} ${year}`;
    // return `Q${q} - ${map[q]} ${year}`;
  };
  // no manual quarter handler; it is derived from month

  useEffect(() => {
    if (!filters?.startDate || !filters?.endDate || !filters?.monthYear) {
      const startDate = dayjs().subtract(1, "week").format("DD-MM-YYYY");
      const endDate = dayjs().format("DD-MM-YYYY");
      const monthYear = dayjs().format("MM-YYYY");

      const monthYearDayjs = dayjs(monthYear, "MM-YYYY");
      setMonthYear(monthYearDayjs);
      const derivedQuarter = `Q${Math.ceil(
        Number(monthYearDayjs.format("M")) / 3
      )}-${monthYearDayjs.format("YYYY")}`;
      setFilters({
        ...filters,
        startDate,
        endDate,
        monthYear,
        quarter: derivedQuarter,
      });
    }
  }, [filters, setFilters]);

  return (
    <Flex
      style={{
        // width: "100%",
        width: "auto",
        marginBottom: "1em",
        // marginTop: 0,
        paddingRight: "1em",
        marginTop: "-16px",
      }}
      className="filters-dashboard-main-list"
      justify="center"
      gap={6}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <div>{/* <Text>HOSPITAL</Text> */}</div>
        <Select
          showSearch
          placeholder="Select a Hospital"
          optionFilterProp="label"
          style={{ width: 150 }}
          value={selectedHospital ? selectedHospital.value : undefined}
          options={
            availableHospitals &&
            availableHospitals?.map((hospital) => ({
              label: hospital.label,
              value: hospital.value,
            }))
          }
          onChange={handleHospitalChange}
        />
      </div>

      <Flex
        className="dashboard-list-filters"
        style={{
          width: "100%",
          justifyContent: "space-between",
          marginTop: "1rem",
        }}
        gap={6}
      >
        <div
          className="dashboard-list-date-section"
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Flex
            className="dashboard-list-date-buttons"
            gap={12}
            style={{
              marginTop: "0.5rem",
              flexWrap: "wrap",
            }}
          >
            <div
              className="columnButton"
              style={{ display: "flex", alignItems: "center" }}
            >
              <Dropdown
                overlay={menu}
                trigger={["click"]}
                open={dropdownVisible}
                onOpenChange={(visible) => {
                  setDropdownVisible(visible);
                }}
                menu={{ selectable: false }}
                className="custom-dropdown-scale"
              >
                <Tooltip title="Column Selector">
                  <Button icon={<ColumnSelection />} />
                </Tooltip>
              </Dropdown>
            </div>
            {/* <RangePicker
              value={dateRange}
              onChange={(dates) => {
                handleDateRangeChange(dates);
              }}
              format="DD-MM-YYYY"
              style={{ border: "0.4px solid #222222", width: "230px" }}
              allowClear={true}
            /> */}
            <DatePicker
              value={monthYear}
              picker="month"
              //   format={"MM-YYYY"}
              format={(value) => (value ? value.format("MMMM") : "")}
              onChange={(month) => handleMonthYearChange(month)}
              allowClear={true}
            />
            <DatePicker
              value={
                filters?.quarter
                  ? (() => {
                      const [q, y] = String(filters.quarter).split("-");
                      const quarterNumber = Number(q.replace("Q", ""));
                      const year = Number(y);
                      const startMonth = (quarterNumber - 1) * 3 + 1; // 1,4,7,10
                      return dayjs(`${year}-${startMonth}-01`, "YYYY-M-D");
                    })()
                  : null
              }
              picker="quarter"
              format={(value) =>
                value
                  ? getQuarterLabel(
                      Math.ceil(Number(value.format("M")) / 3),
                      Number(value.format("YYYY"))
                    )
                  : ""
              }
              disabled
              allowClear={false}
            />
          </Flex>
        </div>
      </Flex>
    </Flex>
  );
};

export default FiltersManagementDashboard;
